import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Divider,
  Alert,
  Box,
  Chip,
  IconButton,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import Sidebar from '../Sidebar/Sidebar';
import useAuth from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  get2FASetup,
  verify2FACode,
  disable2FA,
} from '../../services/twoFactorService';

const TwoFactorSettingsPage = () => {
  const { isLoggedIn, user } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState('');
  const [show2FAForm, setShow2FAForm] = useState(false);

  const [loading, setLoading] = useState(false); // for enable/disable
  const [verifyLoading, setVerifyLoading] = useState(false); // for verify button
  const [error, setError] = useState(null);

  // Local mirror of 2FA status, since we don't have setUser from useAuth
  const [is2FAEnabled, setIs2FAEnabled] = useState(!!user?.is_2fa_enabled);

  useEffect(() => {
    setIs2FAEnabled(!!user?.is_2fa_enabled);
  }, [user]);

  const handleEnable2FA = async () => {
    setError(null);
    setLoading(true);
    try {
      const qr = await get2FASetup();
      setQrData(qr);
      setShow2FAForm(true);
    } catch (e) {
      const msg = t(
        'profile.2fa_setup_failed',
        'Nepodarilo sa inicializovať nastavenie 2FA.'
      );
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    setVerifyLoading(true);
    try {
      const res = await verify2FACode(code);
      if (res.success) {
        enqueueSnackbar(
          t('profile.2fa_enabled', 'Dvojfaktorové overovanie bolo zapnuté.'),
          {
            variant: 'success',
          }
        );
        setIs2FAEnabled(true); // local update
        setShow2FAForm(false);
        setQrData(null);
        setCode('');
      } else {
        const msg = t(
          'profile.invalid_code',
          'Zadaný kód je neplatný.'
        );
        setError(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } catch (e) {
      const msg = t(
        'profile.2fa_enable_failed',
        'Nepodarilo sa zapnúť 2FA.'
      );
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await disable2FA();
      if (res.success) {
        enqueueSnackbar(
          t('profile.2fa_disabled', 'Dvojfaktorové overovanie bolo vypnuté.'),
          {
            variant: 'success',
          }
        );
        setIs2FAEnabled(false); // local update
        setShow2FAForm(false);
        setQrData(null);
        setCode('');
      } else {
        const msg = t(
          'profile.2fa_disable_failed',
          'Nepodarilo sa vypnúť 2FA.'
        );
        setError(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } catch (e) {
      const msg = t(
        'profile.2fa_disable_failed',
        'Nepodarilo sa vypnúť 2FA.'
      );
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const copySecretToClipboard = () => {
    if (qrData?.secret) {
      navigator.clipboard.writeText(qrData.secret);
      enqueueSnackbar(
        t('profile.secret_copied', 'Tajný kľúč bol skopírovaný.'),
        {
          variant: 'info',
        }
      );
    }
  };

  if (!isLoggedIn || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Sidebar user={user} />

      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Header */}
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                {t(
                  'profile.2fa_title',
                  'Dvojfaktorové overovanie (2FA)'
                )}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {t(
                  'profile.2fa_subtitle',
                  'Zapnutím dvojfaktorového overovania pridáte k svojmu účtu ďalšiu vrstvu zabezpečenia.'
                )}
              </Typography>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" sx={{ mr: 1 }}>
                  {t('profile.2fa_status', 'Stav 2FA:')}
                </Typography>
                <Chip
                  label={
                    is2FAEnabled
                      ? t('profile.2fa_on', 'Zapnuté')
                      : t('profile.2fa_off', 'Vypnuté')
                  }
                  color={is2FAEnabled ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {is2FAEnabled
                  ? t(
                      'profile.2fa_status_enabled_help',
                      'Pri prihlasovaní budete vyzvaný na zadanie 6-miestneho kódu z autentifikačnej aplikácie.'
                    )
                  : t(
                      'profile.2fa_status_disabled_help',
                      'Odporúčame zapnúť dvojfaktorové overovanie, aby ste svoj účet ochránili pred neoprávneným prístupom.'
                    )}
              </Typography>
            </Grid>

            {/* Error */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}

            {/* Action buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {is2FAEnabled ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDisable2FA}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={22} />
                    ) : (
                      t('profile.disable_2fa', 'Vypnúť 2FA')
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleEnable2FA}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={22} />
                    ) : (
                      t('profile.enable_2fa', 'Zapnúť 2FA')
                    )}
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Setup form (only when enabling) */}
            {!is2FAEnabled && show2FAForm && qrData && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                  {/* QR + secret */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      {t(
                        'profile.scan_qr_title',
                        'Krok 1: Naskenujte QR kód'
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1.5 }}
                      color="text.secondary"
                    >
                      {t(
                        'profile.scan_qr',
                        'Naskenujte nižšie uvedený QR kód v aplikácii Google Authenticator alebo inej kompatibilnej aplikácii.'
                      )}
                    </Typography>

                    <Box sx={{ textAlign: 'center' }}>
                      <img
                        src={`data:image/png;base64,${qrData.qr_code_base64}`}
                        alt="2FA QR Code"
                        style={{ width: 200, marginBottom: 16 }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold', mb: 0.5 }}
                    >
                      {t(
                        'profile.secret_key_label',
                        'Tajný kľúč (manuálne zadanie):'
                      )}
                    </Typography>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: '#222',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                        mb: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                        color: '#fff',
                      }}
                    >
                      {qrData.secret}
                      <IconButton
                        size="small"
                        color="inherit"
                        sx={{ ml: 1 }}
                        onClick={copySecretToClipboard}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </Grid>

                  {/* Code input */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      {t(
                        'profile.enter_code_step',
                        'Krok 2: Potvrďte svoj kód'
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1 }}
                      color="text.secondary"
                    >
                      {t(
                        'profile.enter_code_prompt',
                        'Zadajte aktuálny 6-miestny kód z autentifikačnej aplikácie a dokončite zapnutie 2FA.'
                      )}
                    </Typography>

                    <TextField
                      label={t('profile.enter_code', '2FA kód')}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ mb: 2, maxWidth: 260 }}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 6,
                      }}
                    />

                    <Button
                      variant="contained"
                      onClick={handleVerifyCode}
                      disabled={verifyLoading || !code}
                    >
                      {verifyLoading ? (
                        <CircularProgress size={22} />
                      ) : (
                        t('profile.verify', 'Overiť kód')
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Container>
    </>
  );
};

export default TwoFactorSettingsPage;
